import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Sparkles, Zap, Layers, Cloud, Server, Wrench, Shuffle, FileCheck2 } from 'lucide-react';
import PrinterStrip from './PrinterStrip';
import BrandLogo from './BrandLogo';

const STARS = [
  {
    id: 'printfactory',
    slug: '/printfactory',
    tagline: 'Independent software written by printers, for printers',
    name: 'PrintFactory',
    brandKey: 'printfactory',
    accent: '#00B7C7',
    accentSoft: 'rgba(0, 183, 199, 0.14)',
    logo: '/logos/printfactory.png',
    heroImage: 'https://images.unsplash.com/photo-1693031630369-bd429a57f115?w=1200&q=85',
    kicker: 'Cloud Color Workflow',
    stat: '150,000+',
    statLabel: 'satisfied printers',
    features: [
      { Icon: Cloud, label: 'Cloud color server' },
      { Icon: Zap, label: 'Automated calibration' },
      { Icon: Server, label: 'Multi-device RIP' },
      { Icon: Wrench, label: 'End-to-end workflow' },
    ],
    description:
      'A complete color management workflow that handles calibration, output profiles and job automation across your entire fleet — from proofing to production.',
  },
  {
    id: 'impoproof',
    slug: '/impoproof',
    tagline: 'The premier double-sided proofing solution in the world',
    name: 'ImpoProof',
    accent: '#E5007E',
    accentSoft: 'rgba(229, 0, 126, 0.14)',
    logo: 'https://www.proofnation.com/wp-content/uploads/2023/11/Captura-de-tela-2023-11-21-182445.png',
    heroImage: 'https://images.pexels.com/photos/1440504/pexels-photo-1440504.jpeg?w=1200',
    kicker: 'Double-Sided Proofing',
    stat: 'Front & Back',
    statLabel: 'in a single sheet',
    features: [
      { Icon: Layers, label: 'Double-sided output' },
      { Icon: Shuffle, label: 'Imposition preview' },
      { Icon: FileCheck2, label: 'Registration verified' },
      { Icon: Sparkles, label: 'Labels & packaging' },
    ],
    description:
      'Print both sides of your proof in perfect registration to verify layout, folding and imposition — the way it will appear in production.',
  },
];

const StarProducts = () => {
  return (
    <section className="py-28 relative overflow-hidden bg-white" data-testid="star-products-section">
      {/* Ambient dual brand gradient */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 25% 30%, rgba(10,42,99,0.10), transparent 55%),' +
            'radial-gradient(circle at 75% 70%, rgba(255,78,80,0.10), transparent 55%)',
        }}
      />

      {/* Fine grid overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage:
            'linear-gradient(#0A2A63 1px, transparent 1px), linear-gradient(90deg, #0A2A63 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />

      <div className="container-pn relative">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-3 inline-flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5" /> Star Solutions
            </p>
            <h2 className="text-4xl md:text-6xl font-black text-[#050F2E] tracking-tight leading-[1.02]">
              Two flagships that{' '}
              <span className="pn-gradient-text">
                run the industry
              </span>
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              PrintFactory and ImpoProof are the heart of our offering — trusted by print shops across the industry.
            </p>
          </div>
          <div className="max-w-xs">
            <PrinterStrip className="opacity-70" />
          </div>
        </div>

        {/* Star cards */}
        <div className="grid md:grid-cols-2 gap-6">
          {STARS.map((p) => (
            <article
              key={p.id}
              className="relative group rounded-3xl overflow-hidden shadow-xl border border-slate-100 bg-white btn-transition hover:-translate-y-1 hover:shadow-2xl"
            >
              {/* Star badge */}
              <div
                className="absolute top-5 left-5 z-10 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest text-white shadow-lg"
                style={{ backgroundColor: p.accent }}
              >
                <Sparkles className="h-3 w-3" /> Star product
              </div>

              {/* Kicker chip */}
              <div className="absolute top-5 right-5 z-10 inline-flex items-center bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest text-[#0A2A63] shadow-sm">
                {p.kicker}
              </div>

              {/* Hero image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={p.heroImage}
                  alt={p.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                {/* Colored overlay */}
                <div
                  className="absolute inset-0"
                  style={{
                    background: `linear-gradient(180deg, transparent 0%, ${p.accentSoft} 40%, rgba(10,25,41,0.85) 100%)`,
                  }}
                />
                {/* Big stat inside image */}
                <div className="absolute bottom-4 left-5 text-white">
                  <p className="text-[10px] uppercase tracking-widest opacity-70 font-bold">Trusted by</p>
                  <p className="text-3xl font-black tracking-tight">{p.stat}</p>
                  <p className="text-xs opacity-80 -mt-1">{p.statLabel}</p>
                </div>
                {/* Logo */}
                <div className="absolute bottom-4 right-5 bg-white rounded-xl p-2 shadow-lg">
                  <img src={p.logo} alt={`${p.name} logo`} className="h-8 object-contain" />
                </div>
              </div>

              {/* Body */}
              <div className="p-7">
                <p className="text-xs uppercase tracking-widest font-bold" style={{ color: p.accent }}>{p.tagline}</p>
                {p.brandKey ? (
                  <BrandLogo brand={p.brandKey} imgClassName="h-10" className="mt-3" testId={`star-title-logo-${p.id}`} />
                ) : (
                  <h3 className="mt-2 text-3xl font-black tracking-tight text-[#050F2E]">{p.name}</h3>
                )}
                <p className="mt-3 text-slate-600 leading-relaxed">{p.description}</p>

                {/* Features */}
                <ul className="mt-6 grid grid-cols-2 gap-3">
                  {p.features.map(({ Icon, label }) => (
                    <li key={label} className="flex items-center gap-2.5">
                      <span
                        className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: p.accentSoft }}
                      >
                        <Icon className="h-4 w-4" style={{ color: p.accent }} />
                      </span>
                      <span className="text-sm font-semibold text-slate-700">{label}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <div className="mt-7 flex items-center justify-between">
                  <Link to={p.slug}>
                    <Button className="bg-[#0A2A63] hover:bg-[#061a3f] text-white rounded-full h-11 px-6 font-bold btn-transition group/btn">
                      Learn more
                      <ArrowRight className="h-4 w-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                  <div className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full animate-soft-glow" style={{ backgroundColor: p.accent, color: p.accent }} />
                    <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Live · v22</span>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StarProducts;
